import React, { createContext, useContext, useState, useEffect } from 'react';
import { getGameStats, setGameStats } from '../utils/storage';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'reviewer' | 'admin';
  stats?: any;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateStatsOnServer: (stats: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_NEW_WORDS_API_URL || 'http://localhost:8080';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Sync token and load user profile on startup
  useEffect(() => {
    const savedToken = localStorage.getItem('zwizle_token');
    if (savedToken) {
      setToken(savedToken);
      fetchProfile(savedToken).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProfile = async (authToken: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/profile`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        // Token might be invalid/expired
        logout();
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const migrateLocalStats = async (authToken: string, serverUser: User) => {
    try {
      const localStats = getGameStats();
      // Only migrate if player has actually played games locally
      if (localStats && localStats.gamesPlayed > 0) {
        const res = await fetch(`${API_BASE_URL}/api/users/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            stats: {
              currentStreak: localStats.currentStreak || 0,
              longestStreak: localStats.maxStreak || 0,
              totalGamesPlayed: localStats.gamesPlayed || 0,
              guessDistribution: Object.keys(localStats.guessDistribution || {}).length > 0 
                ? [1,2,3,4,5,6,7].map(g => localStats.guessDistribution[g] || 0)
                : [0, 0, 0, 0, 0, 0, 0]
            }
          })
        });

        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          console.log('Local stats migrated successfully');
        }
      } else if (serverUser.stats) {
        // Sync server stats down to local storage if local is empty
        const mappedStats = {
          gamesPlayed: serverUser.stats.totalGamesPlayed || 0,
          gamesWon: serverUser.stats.totalGamesPlayed - (serverUser.stats.guessDistribution?.[6] || 0), // guessDistribution index 6 is failures or similar
          currentStreak: serverUser.stats.currentStreak || 0,
          maxStreak: serverUser.stats.longestStreak || 0,
          guessDistribution: {} as Record<number, number>
        };
        serverUser.stats.guessDistribution?.forEach((count: number, index: number) => {
          mappedStats.guessDistribution[index + 1] = count;
        });
        setGameStats(mappedStats);
      }
    } catch (err) {
      console.error('Error migrating stats:', err);
    }
  };

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API_BASE_URL}/api/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }

    localStorage.setItem('zwizle_token', data.token);
    setToken(data.token);
    setUser(data.user);

    // Prompt user/migrate stats
    await migrateLocalStats(data.token, data.user);
  };

  const register = async (name: string, email: string, password: string) => {
    const res = await fetch(`${API_BASE_URL}/api/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name, email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    localStorage.setItem('zwizle_token', data.token);
    setToken(data.token);
    setUser(data.user);

    // Migrate local stats to the newly created account
    await migrateLocalStats(data.token, data.user);
  };

  const logout = () => {
    localStorage.removeItem('zwizle_token');
    setToken(null);
    setUser(null);
  };

  const updateStatsOnServer = async (stats: any) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stats })
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      }
    } catch (err) {
      console.error('Error syncing stats to server:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateStatsOnServer }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
